import { useState, FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, Lock, CheckCircle, AlertTriangle } from 'lucide-react';
import LogoIcon from '../components/LogoIcon';
import { resetPassword } from '../services/api';
import toast from 'react-hot-toast';

export default function ResetPasswordPage() {
    const [searchParams]          = useSearchParams();
    const navigate                = useNavigate();
    const token                   = searchParams.get('token') || '';

    const [password,    setPassword]    = useState('');
    const [confirm,     setConfirm]     = useState('');
    const [showPw,      setShowPw]      = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [done,        setDone]        = useState(false);

    const mismatch    = confirm && password !== confirm;
    const tooShort    = password.length > 0 && password.length < 8;
    const canSubmit   = password.length >= 8 && password === confirm && !loading;

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
                <div className="w-full max-w-sm">
                    <div className="card text-center py-8">
                        <AlertTriangle size={40} className="text-red-400 mx-auto mb-4" />
                        <h2 className="text-lg font-semibold text-white mb-2">链接无效</h2>
                        <p className="text-slate-400 text-sm mb-6">
                            重置链接无效或已过期，请重新申请。
                        </p>
                        <Link to="/forgot-password" className="btn-primary">重新申请</Link>
                    </div>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        try {
            await resetPassword(token, password);
            setDone(true);
            toast.success('密码重置成功');
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })
                ?.response?.data?.error || '重置失败，链接可能已过期，请重新申请';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4 shadow-lg shadow-brand-900/50 rounded-2xl">
                        <LogoIcon size={48} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">税码通</h1>
                    <p className="text-slate-500 text-sm mt-1">AI 智能 HTS 编码分类平台</p>
                </div>

                <div className="card">
                    {done ? (
                        <div className="text-center py-4">
                            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
                            <h2 className="text-lg font-semibold text-white mb-2">密码已重置</h2>
                            <p className="text-slate-400 text-sm mb-6">
                                新密码已生效，请使用新密码登录。
                            </p>
                            <button
                                onClick={() => navigate('/login', { replace: true })}
                                className="btn-primary w-full"
                            >
                                前往登录
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <Lock size={16} className="text-brand-400" />
                                <h2 className="text-lg font-semibold text-white">设置新密码</h2>
                            </div>
                            <p className="text-slate-500 text-sm mb-6">
                                请输入新密码，长度至少 8 位
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                        新密码
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPw ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="至少 8 位"
                                            required
                                            autoFocus
                                            className={`input-field pr-10 ${tooShort ? 'border-red-500/50' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((v) => !v)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                                        >
                                            {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                                        </button>
                                    </div>
                                    {tooShort && (
                                        <p className="text-xs text-red-400 mt-1">密码至少需要 8 位</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                        确认新密码
                                    </label>
                                    <input
                                        type={showPw ? 'text' : 'password'}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        placeholder="再次输入新密码"
                                        required
                                        className={`input-field ${mismatch ? 'border-red-500/50' : ''}`}
                                    />
                                    {mismatch && (
                                        <p className="text-xs text-red-400 mt-1">两次输入的密码不一致</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="btn-primary w-full mt-2"
                                >
                                    {loading ? (
                                        <><Loader2 size={16} className="animate-spin" /> 重置中...</>
                                    ) : (
                                        '确认重置密码'
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    <p className="text-center text-sm text-slate-500 mt-5">
                        <Link to="/login" className="text-slate-400 hover:text-white transition-colors">
                            返回登录
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
